import React, { useEffect, useState } from "react";
import { MonitorSmartphone } from "lucide-react";
import {
  SESSION_REVOKED_EVENT,
  type SessionRevokedDetail,
} from "../../utils/sessionEvents";

/**
 * Shown on the device that just lost its session because the account signed in
 * somewhere else. Deliberately blocking and deliberately not an auto-redirect —
 * a hard nav would throw away whatever the user had half-typed into a shipment
 * form without ever telling them why.
 */
const SessionExpiredModal: React.FC = () => {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const onRevoked = (event: Event) => {
      const detail = (event as CustomEvent<SessionRevokedDetail>).detail;
      setMessage(detail?.message ?? "Your session has expired.");
    };

    window.addEventListener(SESSION_REVOKED_EVENT, onRevoked);
    return () => window.removeEventListener(SESSION_REVOKED_EVENT, onRevoked);
  }, []);

  if (!message) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <MonitorSmartphone className="h-7 w-7 text-amber-600" />
        </div>

        <h2
          id="session-expired-title"
          className="text-lg font-bold text-gray-900"
        >
          Session Expired
        </h2>

        <p className="text-sm text-gray-600 mt-2 leading-relaxed">{message}</p>

        <p className="text-xs text-gray-400 mt-3">
          Only one device can be signed in to an account at a time. Sign in
          again to continue here.
        </p>

        <button
          type="button"
          onClick={() => {
            window.location.href = "/login";
          }}
          className="mt-6 w-full px-4 py-2.5 bg-[#C97A3C] text-white rounded-lg font-medium hover:bg-[#b56a30] transition-colors"
        >
          Sign in again
        </button>
      </div>
    </div>
  );
};

export default SessionExpiredModal;
